"use client";

import { PaneContent } from "./PaneContent";
import { DEFAULT_PANE_POSITION, PaneGroupContext } from "./PaneGroupContext";
import { PaneOverlay } from "./PaneOverlay";
import { PAGE_HIGHLIGHT_SURFACE_CLASS } from "./surface";
import { usePaneLifecycle } from "./usePaneLifecycle";
import {
  usePanePresentation,
  type ResponsivePaneOptions,
} from "./usePanePresentation";
import { useResizableRailWidth } from "./useResizableRailWidth";
import { cn } from "@/lib/utils";
import {
  useContext,
  useId,
  useLayoutEffect,
  type ElementType,
  type MouseEvent,
  type ReactNode,
} from "react";

type PaneResizeOptions = {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
};

type PaneSizing =
  | {
      resize: PaneResizeOptions;
      width?: never;
      resizable?: never;
      onResizeStart?: never;
    }
  | {
      resize?: never;
      width?: number | string;
      resizable?: boolean;
      onResizeStart?: (event: MouseEvent) => void;
    };

export type PagePaneProps = PaneSizing & {
  children: ReactNode;
  ariaLabel?: string;
  as?: ElementType;
  className?: string;
  contentClassName?: string;
  flex?: boolean;
  inertWhenClosed?: boolean;
  keepMounted?: boolean;
  open?: boolean;
  resizeHandleSide?: "left" | "right";
  surface?: "primary" | "highlight";
  zIndex?: number;
  animate?: boolean;
} & (
    | { responsive: ResponsivePaneOptions; onClose: () => void }
    | { responsive?: never; onClose?: () => void }
  );

export const PagePane = ({
  children,
  ariaLabel,
  as = "section",
  className,
  contentClassName,
  flex = false,
  inertWhenClosed = true,
  keepMounted = false,
  open = true,
  resize,
  resizable = false,
  resizeHandleSide = "right",
  surface = "primary",
  width,
  zIndex,
  animate = true,
  onResizeStart,
  responsive,
  onClose,
}: PagePaneProps) => {
  const ctx = useContext(PaneGroupContext);
  const id = useId();
  const { isMounted, isExpanded } = usePaneLifecycle(open);
  const presentation = usePanePresentation(responsive);
  const {
    widthStyle,
    isResizing,
    onResizeStart: startResize,
  } = useResizableRailWidth({
    defaultWidth: resize?.defaultWidth ?? 0,
    minWidth: resize?.minWidth ?? 0,
    maxWidth: resize?.maxWidth ?? 0,
    side: resizeHandleSide === "left" ? "right" : "left",
  });
  const { hasPreviousPane } = ctx?.positions.get(id) ?? DEFAULT_PANE_POSITION;

  useLayoutEffect(() => {
    ctx?.notifyChange();
  }, [ctx, isExpanded, isMounted, presentation]);

  if (!isMounted && !keepMounted) return null;

  const fixedWidth = resize ? widthStyle : width;
  const isFixedWidth = fixedWidth != null && !flex;
  const surfaceClassName =
    surface === "highlight"
      ? `${PAGE_HIGHLIGHT_SURFACE_CLASS} bg-surface-highlight`
      : "bg-surface-primary";
  const isInline = presentation === "inline";
  const content = (
    <PaneContent
      as={as}
      ariaLabel={ariaLabel}
      width={isInline && isFixedWidth ? fixedWidth : undefined}
      className={cn(
        surfaceClassName,
        isInline && hasPreviousPane && "border-l border-surface-tertiary",
        className
      )}
      isVisible={isInline ? isExpanded : open}
      contentClassName={contentClassName}
    >
      {children}
    </PaneContent>
  );

  if (presentation !== "inline" && onClose) {
    return (
      <PaneOverlay
        open={open}
        presentation={presentation}
        width={fixedWidth}
        ariaLabel={ariaLabel}
        onClose={onClose}
      >
        {content}
      </PaneOverlay>
    );
  }

  const resizeHandle =
    (resize || resizable) && isExpanded ? (
      <PaneResizeHandle
        ariaLabel={`Resize ${ariaLabel?.toLowerCase() ?? "pane"}`}
        side={resizeHandleSide}
        onMouseDown={resize ? startResize : onResizeStart}
      />
    ) : null;

  return (
    <>
      {resizeHandleSide === "left" && resizeHandle}
      <div
        aria-hidden={!isExpanded}
        inert={!isExpanded && inertWhenClosed ? true : undefined}
        data-pane-group={ctx?.groupId}
        data-pane-id={id}
        data-pane-visible={isMounted}
        data-pane-expanded={isExpanded}
        className={cn(
          "h-full min-w-0 shrink-0 overflow-hidden",
          surfaceClassName,
          animate &&
            !isResizing &&
            "transition-[width] duration-300 ease-in-out will-change-[width]",
          flex && isExpanded && "flex-1",
          !isExpanded && "pointer-events-none"
        )}
        style={{
          flex: flex && isExpanded ? "1 1 0%" : "0 0 auto",
          minWidth: flex && isExpanded ? 0 : undefined,
          width: isFixedWidth ? (isExpanded ? fixedWidth : 0) : undefined,
          zIndex,
        }}
      >
        {content}
      </div>
      {resizeHandleSide === "right" && resizeHandle}
    </>
  );
};

const PaneResizeHandle = ({
  side,
  ariaLabel,
  onMouseDown,
}: {
  side: "left" | "right";
  ariaLabel: string;
  onMouseDown?: (event: MouseEvent) => void;
}) => (
  <div className="relative z-30 w-0 shrink-0">
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      onMouseDown={onMouseDown}
      className={cn(
        "absolute top-0 flex h-full w-[16px] cursor-col-resize items-center justify-center group/handle",
        side === "left" ? "-right-[8px]" : "-left-[8px]"
      )}
    >
      <div className="h-full max-h-[64px] w-[2px] rounded-full bg-transparent transition-colors duration-150 group-hover/handle:bg-border-secondary" />
    </div>
  </div>
);
